import {StreamEvent, StreamObserver, StreamState} from './types';

/**
 * StreamController acts as the event-bus for all streaming
 * operations. It keeps track of:
 *
 * • {@link observers} – passive listeners that inspect every chunk to record, collect data, etc.
 *   They implement the {@link StreamObserver} contract and are notified sequentially for each life-cycle event.
 *
 * • {@link listeners} – event handlers registered via to react to specific events [.on('data) , on('end') ]
 *
 * The class purposefully does NOT implement any transport logic – that is left to higher-level orchestrators (e.g. {@link ClientStream} or {@link StreamOrchestrator}).
 *
 */
export class StreamController<ChunkType = StreamEvent> {
  protected observers: Map<string, StreamObserver<ChunkType>> = new Map();
  protected listeners: Map<string, Array<(data: unknown) => void>> = new Map();
  protected logger: (msg: string, meta?: unknown) => void;

  private state: StreamState = 'idle';
  private lifecycleFlags = {
    startNotified: false,
    endNotified: false,
  };

  constructor(logger?: (msg: string, meta?: unknown) => void) {
    this.logger = logger ?? (() => {});
  }

  getState(): StreamState {
    return this.state;
  }

  protected async transition(newState: StreamState): Promise<void> {
    const validTransitions: Record<StreamState, StreamState[]> = {
      idle: ['active'],
      active: ['ended', 'error'],
      ended: [],
      error: [],
    };

    if (!validTransitions[this.state]?.includes(newState)) {
      throw new Error(`Invalid state transition: ${this.state} -> ${newState}`);
    }

    const oldState = this.state;
    this.state = newState;

    // Handle lifecycle notifications
    if (
      oldState === 'idle' &&
      newState === 'active' &&
      !this.lifecycleFlags.startNotified
    ) {
      this.lifecycleFlags.startNotified = true;
      await this.notifyLifecycle('onStart');
    } else if (
      oldState === 'active' &&
      newState === 'ended' &&
      !this.lifecycleFlags.endNotified
    ) {
      this.lifecycleFlags.endNotified = true;
      await this.notifyLifecycle('onEnd');
    }

    this.logger(`State transition: ${oldState} -> ${newState}`);
    this.emit('stateChange', {from: oldState, to: newState});
  }

  subscribe(observer: StreamObserver<ChunkType>): void {
    this.observers.set(observer.name, observer);
    observer.setController(this);
    this.logger(`Subscribed observer: ${observer.name}`);
  }

  unsubscribe(name: string): void {
    const obs = this.observers.get(name);
    if (obs) {
      try {
        obs.onEnd?.();
      } catch (err) {
        this.logger(`Observer '${name}' onEnd failed during unsubscribe`, {
          error: err,
        });
      } finally {
        obs.setController(undefined);
        this.observers.delete(name);
      }
    } else {
      this.observers.delete(name);
    }
    this.logger(`Unsubscribed observer: ${name}`);
  }

  hasObserver(name: string): boolean {
    return this.observers.has(name);
  }

  getObserver<T extends StreamObserver>(name: string): T {
    const obs = this.observers.get(name);
    if (!obs) throw new Error(`Observer with name ${name} not found`);
    return obs as T;
  }

  on<T = unknown>(event: string, handler: (data: T) => void): this {
    const handlers = this.listeners.get(event) ?? [];
    handlers.push(handler as (data: unknown) => void);
    this.listeners.set(event, handlers);
    return this;
  }

  off<T = unknown>(event: string, handler?: (data: T) => void): this {
    if (!handler) {
      this.listeners.delete(event);
      return this;
    }
    const handlers = this.listeners.get(event) ?? [];
    const index = handlers.indexOf(handler as (data: unknown) => void);
    if (index > -1) {
      handlers.splice(index, 1);
      if (handlers.length === 0) this.listeners.delete(event);
      else this.listeners.set(event, handlers);
    }
    return this;
  }

  protected emit(event: string, payload: unknown): void {
    const evListeners = this.listeners.get(event);
    if (!evListeners) return;
    for (const cb of evListeners) {
      try {
        cb(payload);
      } catch (err) {
        this.logger(`Event listener for '${event}' failed`, {error: err});
      }
    }
  }

  private async notifyAllObservers(
    operation: (
      observer: StreamObserver<ChunkType>,
      name: string
    ) => Promise<void> | void,
    operationName: string,
    waitForCompletion = false
  ): Promise<void> {
    const promises: Promise<void>[] = [];

    this.observers.forEach((observer, name) => {
      const promise = (async () => {
        try {
          await operation(observer, name);
        } catch (err) {
          this.logger(`Observer '${name}' ${operationName} failed`, {
            error: err,
          });
          observer.onError?.(err);
        }
      })();

      promises.push(promise);
    });

    if (waitForCompletion) {
      await Promise.allSettled(promises);
    } else {
      // Fire and forget
      Promise.allSettled(promises);
    }
  }

  protected notifyObservers(chunk: ChunkType): void {
    this.notifyAllObservers(
      (observer) => observer.onChunk(chunk),
      'processing chunk'
    );
  }

  protected async notifyLifecycle(hook: 'onStart' | 'onEnd'): Promise<void> {
    await this.notifyAllObservers(
      (observer) => observer[hook]?.(),
      `lifecycle hook '${hook}'`,
      true
    );
  }

  protected notifyObserversError(error: unknown): void {
    this.notifyAllObservers(
      (observer) => observer.onError?.(error),
      'error handler'
    );
  }
}
